import type { LogLevel } from '@apiary/shared'
import type { BotEventDto, BotEventQuery } from '@/lib/dto'

/**
 * Фильтры консоли логов — чистые функции, без React.
 *
 * Живут отдельным модулем по двум причинам: их проверяет юнит-тест без рендера,
 * и ими пользуются оба потребителя — сам список на экране и запрос CSV-экспорта.
 * Второе важнее: выгрузка, не совпадающая с тем, что человек видит на экране, —
 * самый обидный вид расхождения.
 *
 * Фильтрация уровня и поиска делается НА КЛИЕНТЕ, а не через queryKey. Причина в
 * `ws-cache.applyBotEvent`: живые строки дописываются в кэш по ключу
 * `qk.bots.events(botId)`, и если фильтр попадёт в ключ, каждое его изменение
 * выбрасывало бы уже накопленный live-буфер и обнуляло консоль.
 */

/** Уровень или ГРУППА уровней: оператору нужен «warn и хуже», а не пять галочек. */
export type LevelFilter = 'all' | 'noDebug' | 'chat' | 'warn' | 'error'

export const LEVEL_FILTERS: { value: LevelFilter; label: string; note?: string }[] = [
  { value: 'all', label: 'Все уровни' },
  { value: 'noDebug', label: 'Без отладки', note: 'INFO+' },
  { value: 'chat', label: 'Только чат', note: 'CHAT' },
  { value: 'warn', label: 'Предупреждения и ошибки', note: 'WARN+' },
  { value: 'error', label: 'Только ошибки', note: 'ERROR' }
]

export function matchesLevel(level: LogLevel, filter: LevelFilter): boolean {
  switch (filter) {
    case 'all':
      return true
    case 'noDebug':
      return level !== 'DEBUG'
    case 'chat':
      return level === 'CHAT'
    case 'warn':
      return level === 'WARN' || level === 'ERROR'
    case 'error':
      return level === 'ERROR'
  }
}

/** Диапазон времени. `all` — весь буфер, остальное — «последние N». */
export type RangeFilter = 'all' | '15m' | '1h' | '6h' | '24h'

export const RANGE_FILTERS: { value: RangeFilter; label: string }[] = [
  { value: 'all', label: 'За всё время' },
  { value: '15m', label: 'Последние 15 минут' },
  { value: '1h', label: 'Последний час' },
  { value: '6h', label: 'Последние 6 часов' },
  { value: '24h', label: 'Последние сутки' }
]

const RANGE_MS: Record<Exclude<RangeFilter, 'all'>, number> = {
  '15m': 15 * 60_000,
  '1h': 3_600_000,
  '6h': 6 * 3_600_000,
  '24h': 24 * 3_600_000
}

/** Начало диапазона в ISO — то же значение уходит в `?from=` при экспорте. */
export function rangeStart(range: RangeFilter, now = Date.now()): string | null {
  if (range === 'all') return null
  return new Date(now - RANGE_MS[range]).toISOString()
}

export interface LogQuery {
  level: LevelFilter
  /** Вид события (`chat`, `hook`, `kick`, …). Пустая строка — любой. */
  kind: string
  search: string
  range: RangeFilter
}

export const EMPTY_LOG_QUERY: LogQuery = { level: 'all', kind: '', search: '', range: 'all' }

export function matchesSearch(event: BotEventDto, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === '') return true
  return event.message.toLowerCase().includes(needle) || event.kind.toLowerCase().includes(needle)
}

/** Полный фильтр строки лога. `now` параметром — чтобы тест не зависел от часов. */
export function matchesLogQuery(event: BotEventDto, query: LogQuery, now = Date.now()): boolean {
  if (!matchesLevel(event.level, query.level)) return false
  if (query.kind !== '' && event.kind !== query.kind) return false
  const from = rangeStart(query.range, now)
  if (from !== null && event.at < from) return false
  return matchesSearch(event, query.search)
}

export function filterLog(
  events: readonly BotEventDto[],
  query: LogQuery,
  now = Date.now()
): BotEventDto[] {
  return events.filter(event => matchesLogQuery(event, query, now))
}

/** Единственный уровень фильтра, если он выражается через `?level=` API. */
const EXACT_LEVEL: Partial<Record<LevelFilter, LogLevel>> = { chat: 'CHAT', error: 'ERROR' }

/**
 * Тот же фильтр в терминах `GET /bots/:id/events/export` (docs/06 §5).
 *
 * Группы уровней (`Без отладки`, `WARN+`) контракт не выражает: `?level=` берёт
 * ровно один уровень. Для них уровень в запрос не уходит — и экран обязан честно
 * об этом сказать (`levelNotExportable`), а не тихо отдать другой CSV.
 */
export function toExportQuery(query: LogQuery, now = Date.now()): BotEventQuery {
  const level = EXACT_LEVEL[query.level]
  const from = rangeStart(query.range, now)
  return {
    ...(level === undefined ? {} : { level }),
    ...(query.kind === '' ? {} : { kind: query.kind }),
    ...(query.search.trim() === '' ? {} : { q: query.search.trim() }),
    ...(from === null ? {} : { from })
  }
}

export function levelNotExportable(level: LevelFilter): boolean {
  return level === 'noDebug' || level === 'warn'
}
